import { CommonHash } from '@types';
import { validate } from 'rut.js';
import { receiptType, noteType, exportType, purchaseType } from './formUtils';
import {
  referenceDocumentOptions,
  noteTaxReferenceTypes,
  SNA_REFERENCE_TYPE,
  SNA_REQUIRED_SERVICE_INDICATORS,
} from '@constants';

function validReferenceDate(refDateStr: string) {
  const refDate = new Date(refDateStr);
  const today = new Date();
  return refDate <= today;
}

export function referenceDateValidation(value: string) {
  // if (!noteType(values.document.type)) return null;
  if (value !== '' && validReferenceDate(value)) return null;
  if (value === '') return 'Este campo es requerido';
  return 'La fecha no puede ser posterior a la fecha del documento';
}

export function emailsValidator(value: string[], required: boolean) {
  if (value === undefined) return;
  const check = value.map((email: string) => emailValidator(email, required));
  if (check.includes('Este campo es requerido')) return 'Este campo es requerido';
  if (check.includes('Email inválido')) return 'Email inválido';
  return;
}
export function emailValidator(value: string, required: boolean) {
  if (value === '' && !required) return;
  if (required) return 'Este campo es requerido';
  const regex = /^[\w.-]+@[a-zA-Z\d.-]+.[a-zA-Z]{2,}$/;
  if (value === '' || regex.test(value)) return null;
  return 'Email inválido';
}

export function paymentTypeValidator(value: string, values: CommonHash) {
  if (
    receiptType(values.document.type) ||
    noteType(values.document.type) ||
    exportType(values.document.type) ||
    values.document.type === 'ElectronicDispatchGuide'
  )
    return null;
  if (!receiptType(values.document.type) && value != '') return null;
  return 'Este campo es requerido';
}
export function kindOfSaleValidator(value: string, values: CommonHash) {
  if (
    receiptType(values.document.type) ||
    noteType(values.document.type) ||
    exportType(values.document.type) ||
    values.document.type === 'ElectronicDispatchGuide'
  )
    return null;
  if (!receiptType(values.document.type) && value != '') return null;
  return 'Este campo es requerido';
}
export function serviceIndicatorValidator(value: string, values: CommonHash) {
  if (exportType(values.document.type)) return exportSnaReferenceValidator(value, values);
  if (!receiptType(values.document.type)) return null;
  if (receiptType(values.document.type) && value != '') return null;
  return 'Este campo es requerido';
}

// El SII acepta con reparos los DTE de exportación de servicios que no referencian la Resolución
// del SNA: "(REF-2-826) REPARO- Export.: Falta la Resolución del SNA". El error se cuelga del
// indicador de servicio porque es el campo que gatilla la exigencia y siempre está en pantalla,
// incluso cuando el documento no tiene ninguna referencia cargada.
function exportSnaReferenceValidator(value: string, values: CommonHash) {
  if (!SNA_REQUIRED_SERVICE_INDICATORS.includes(String(value))) return null;

  const references = values.document.references_attributes.filter(
    (reference: CommonHash) => !reference._destroy,
  );
  if (
    references.some((reference: CommonHash) => reference.original_dte_type === SNA_REFERENCE_TYPE)
  )
    return null;

  return 'La exportación de servicios debe referenciar la Resolución del SNA (tipo 812)';
}

export function taxIdValidator(dteType: string, value: string) {
  if (receiptType(dteType)) return null;
  if (!receiptType(dteType) && value != '' && validate(value)) return null;
  if (value == '') return 'Este campo es requerido';
  return 'El rut del receptor es inválido';
}

export function exportTypeObligatoryValidator(value: string, dteType: string) {
  if (!exportType(dteType)) return null;
  if (value !== '') return null;
  return 'Este campo es requerido';
}

export function companyAdditionalInfoValidator(
  value: string,
  dteType: string,
  values?: CommonHash,
) {
  if (!exportType(dteType) && !purchaseType(dteType)) return null;
  if (purchaseType(dteType) && values?.company?.tax_id != '55555555-5') return null;
  if (value !== '') return null;
  return 'Este campo es requerido';
}
export function issueDateValidator(value: string) {
  if (value === '') return 'Este campo es requerido';
  return null;
}

export function companyValidator(dteType: string, value: string, values?: CommonHash) {
  if (purchaseType(dteType) && values?.company?.tax_id === '55555555-5') {
    return null;
  }

  if (receiptType(dteType) || exportType(dteType)) return null;
  if (!receiptType(dteType) && value != '') return null;
  if (value == '') return 'Este campo es requerido';
  return 'El rut del receptor es inválido';
}

export function referenceDescriptionValidator(value: string, values: CommonHash, path: string) {
  const index = path.split('.')[2];
  if (!values.document.references_attributes[index].global_index) return null;
  if (value !== '') return null;
  return 'Este campo es requerido';
}

export function referenceCodeValidator(value: string, values: CommonHash) {
  if (!noteType(values.document.type)) return null;
  if (value !== '') return null;
  return 'Este campo es requerido';
}

export function referenceOriginalNumberValidator(value: string, values: CommonHash, path: string) {
  const index = path.split('.')[2];
  if (values.document.references_attributes[index].global_index) return null;
  if (value === null) return 'Este campo es requerido';
  // El SII limita FolioRef a 18 caracteres
  if (value.length > 18) return 'El folio no puede tener más de 18 caracteres';
  return null;
}

export function transportRouteValidator(value: string, values: CommonHash) {
  if (!exportType(values.document.type)) return null;
  if (values.document.service_indicator !== '' && value === '')
    return 'Este campo es requerido debido al indicador de servicio seleccionado';
  return null;
}

export function dispatchTypeObligatoryValidator(value: string, dteType: string) {
  if (dteType !== 'ElectronicDispatchGuide') return null;
  if (value !== '') return null;
  return 'Este campo es requerido';
}

// El SII rechaza las notas que referencian un tipo de documento inválido -- p. ej. una guía de
// despacho (52) en una NC (61) -- y las que no referencian ningún documento tributario.
export function referenceDteTypeValidator(value: string, values: CommonHash, path: string) {
  if (value === '' || value === null || value === undefined) return 'Este campo es requerido';

  const dteType = values.document.type;
  if (!noteType(dteType)) return null;

  const allowed = referenceDocumentOptions(dteType).map((option) => option.value);
  if (!allowed.includes(value)) {
    return 'Una nota de crédito o débito no puede referenciar este tipo de documento';
  }

  const taxTypes = noteTaxReferenceTypes(dteType);
  const references = values.document.references_attributes.filter(
    (reference: CommonHash) => !reference._destroy,
  );
  if (references.some((reference: CommonHash) => taxTypes.includes(reference.original_dte_type))) {
    return null;
  }

  // Se muestra solo en la primera línea para no repetir el mismo error en todas.
  const index = Number(path.split('.')[2]);
  if (index !== 0) return null;
  return 'La nota debe referenciar al menos un documento tributario (factura, boleta o nota)';
}
